import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ScannerData } from '../types/ScannerData';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useChartTheme } from '../theme/chartTheme';
import { usePersistentFilters } from '../utils/persistentFilters';
import { useSymbolMapping } from '../contexts/SymbolMappingContext';
import { processOptionsData, getMappedSymbol } from '../utils/optionsUtils';
import { collectDistinctMappedUnderlyings, getFilterOptions } from '../utils/filterUtils';
import ToolExplanation from './ToolExplanation';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import './TradesChartReformed.ds.css';
import StandardFilterBar from './StandardFilterBar';
import { useHistoricalData } from '../hooks/useHistoricalData';
import { HistoricalDataSelector } from './HistoricalDataSelector';
import SentimentBar from './SentimentBar';
import { dataService } from '../services/dataService';
import { computeSpotGammaGexMme, getUnderlyingPriceFallback } from '../utils/gammaCalculations';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import { filterLegacyVolumeScannerData } from '../utils/legacyVolumeChartFilters';
import {
  aggregateLegacyVolumeByStrike,
  filterStrikesByWindow,
} from '../utils/legacyVolumeStrikeAggregation';

interface TradesChartReformedProps {
  data: ScannerData[];
  fullScreen?: boolean;
  hideFilters?: boolean;
  showDataModeFilter?: boolean;
  dataMode?: 'accumulated' | '5min';
  prefilteredScannerRows?: ScannerData[] | null;
  persistentFilterKey?: string;
}

interface PremiumData {
  strike: string;
  boughtCallPremium: number;
  soldCallPremium: number;
  boughtPutPremium: number;
  soldPutPremium: number;
  netCallPremium: number;
  netPutPremium: number;
  totalNetPremium: number;
  callFlowRatio: number;
  putFlowRatio: number;
  sentiment: number;
  underlyingPrice: number;
  netDelta: number;
  boughtCalls: number;
  soldCalls: number;
  boughtPuts: number;
  soldPuts: number;
  underlyingSymbol?: string;
}

const TradesChartReformed: React.FC<TradesChartReformedProps> = ({ 
  data, 
  fullScreen = false, 
  hideFilters = false, 
  showDataModeFilter = true,
  dataMode: propDataMode,
  prefilteredScannerRows = null,
  persistentFilterKey,
}) => {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const t = useChartTheme();
  const { isSymbolMappingEnabled } = useSymbolMapping();
  const { openHistorical } = useHistoricalModal();
  
  // Historical data support
  const { 
    selectedDate, 
    fetchHistoricalData, 
    isHistoricalMode 
  } = useHistoricalData();
  
  // State for historical data
  const [historicalData, setHistoricalData] = useState<ScannerData[]>([]);
  const [, setIsLoadingHistorical] = useState(false);
  
  // Effect to fetch historical data when date changes
  useEffect(() => {
    if (selectedDate) {
      setIsLoadingHistorical(true);
      fetchHistoricalData(selectedDate).then(data => {
        if (data) {
          setHistoricalData(data);
        }
        setIsLoadingHistorical(false);
      });
    } else {
      setHistoricalData([]);
    }
  }, [selectedDate, fetchHistoricalData]);
  
  // Use historical data if in historical mode, otherwise use current data
  const effectiveData = isHistoricalMode ? historicalData : data;

  // Use persistent filters instead of global data mode
  const {
    selectedUnderlying,
    selectedExpiry,
    selectedSide,
    selectedMoneyness,
    selectedDataMode,
    setSelectedUnderlying,
    setSelectedExpiry,
    setSelectedSide,
    setSelectedMoneyness,
    setSelectedDataMode
  } = usePersistentFilters(persistentFilterKey ?? 'TradesChartReformed', propDataMode || '5min');

  const handleStrikeClick = (payload: { strike?: string | number; underlyingSymbol?: string }) => {
    if (payload?.strike == null || !selectedUnderlying) return;
    const optionType = selectedSide === 'put' || selectedSide === 'P' ? 'PUT' : 'CALL';
    openHistorical({
      underlying: (payload.underlyingSymbol || selectedUnderlying) as string,
      strike: Number(payload.strike) || payload.strike,
      expiry: selectedExpiry || undefined,
      optionType,
      source: 'volume',
    });
  };
  const onBarClick = (data: unknown) => {
    const payload = (data as { payload?: { strike?: string | number; underlyingSymbol?: string } })?.payload ?? (data as { strike?: string | number; underlyingSymbol?: string });
    handleStrikeClick(payload);
  };

  // Use persistent filter data mode; when embedded in suite, follow parent prop.
  const effectiveDataMode = hideFilters
    ? (propDataMode || selectedDataMode)
    : selectedDataMode;

  useEffect(() => {
    const onGlobalMode = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: 'accumulated' | '5min' }>).detail?.mode;
      if (mode === 'accumulated' || mode === '5min') {
        setSelectedDataMode(mode);
      }
    };
    window.addEventListener('mmecDataModeChanged', onGlobalMode);
    return () => window.removeEventListener('mmecDataModeChanged', onGlobalMode);
  }, [setSelectedDataMode]);

  const handleDataModeChange = useCallback((mode: 'accumulated' | '5min') => {
    setSelectedDataMode(mode);
    window.dispatchEvent(new CustomEvent('mmecDataModeChanged', { detail: { mode } }));
  }, [setSelectedDataMode]);
  const [showDebugTable, setShowDebugTable] = useState<boolean>(false);
  
  // Category filter state
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'stocks' | 'etfs' | 'indices'>('all');
  
  // Strikes filter state
  const [selectedStrikes, setSelectedStrikes] = useState<'10' | '20' | '30' | 'all' | 'custom'>('all');
  const [customStrikeFrom, setCustomStrikeFrom] = useState<string>('');
  const [customStrikeTo, setCustomStrikeTo] = useState<string>('');

  // CBOE data for Spot Gamma pane (below Delta/Premium)
  const [spotGammaByStrike, setSpotGammaByStrike] = useState<Array<{ strike: string; spotGamma: number }>>([]);
  const [spotGammaFetchKey, setSpotGammaFetchKey] = useState(0);

  const translations = {
    title: language === 'ar' ? 'تحليل تدفق العلاوة التجاري المطور' : 'Trade Premium Flow Reformed Analysis',
    subtitle: language === 'ar' ? 'تحليل تدفق العلاوة عبر سعر التنفيذ المختلفة مع صافي العلاوة' : 'Enhanced premium flow analysis across different strikes with net premium',
    selectExpiry: language === 'ar' ? 'اختر تاريخ اكسبايري' : 'Select Expiry',
    selectTicker: language === 'ar' ? 'اختر السهم' : 'Select Ticker',
    allTickers: language === 'ar' ? 'جميع الأسهم' : 'All Tickers',
    allExpiries: language === 'ar' ? 'جميع التواريخ' : 'All Expiries',
    boughtCallPremium: language === 'ar' ? 'علاوة شراء خيارات الشراء' : 'Bought Call Premium',
    soldCallPremium: language === 'ar' ? 'علاوة بيع خيارات الشراء' : 'Sold Call Premium',
    boughtPutPremium: language === 'ar' ? 'علاوة شراء خيارات البيع' : 'Bought Put Premium',
    soldPutPremium: language === 'ar' ? 'علاوة بيع خيارات البيع' : 'Sold Put Premium',
    netCallPremium: language === 'ar' ? 'صافي علاوة خيارات الشراء' : 'Net Call Premium',
    netPutPremium: language === 'ar' ? 'صافي علاوة خيارات البيع' : 'Net Put Premium',
    totalNetPremium: language === 'ar' ? 'إجمالي صافي العلاوة' : 'Total Net Premium',
    netPremium: language === 'ar' ? 'صافي العلاوة' : 'Net Premium',
    currentPrice: language === 'ar' ? 'السعر الحالي' : 'Current Price',
    sentiment: language === 'ar' ? 'سينتمنت' : 'Sentiment',
    volume: language === 'ar' ? 'الحجم' : 'Volume',
    totalBoughtPremium: language === 'ar' ? 'إجمالي علاوة الشراء' : 'Total Bought Premium',
    totalSoldPremium: language === 'ar' ? 'إجمالي علاوة البيع' : 'Total Sold Premium',
    premiumFlow: language === 'ar' ? 'تدفق العلاوة' : 'Premium Flow',
    premiumAnalysis: language === 'ar' ? 'تحليل العلاوة' : 'Premium Analysis',
    netDelta: language === 'ar' ? 'صافي الدلتا' : 'Net Delta',
    spotGamma: language === 'ar' ? 'سخيار بيع جاما' : 'Spot Gamma',
    bullishPressure: language === 'ar' ? 'ضغط صاعد' : 'Bullish Pressure',
    bearishPressure: language === 'ar' ? 'ضغط هابط' : 'Bearish Pressure',
    debugTable: language === 'ar' ? 'جدول التصحيح' : 'Debug Table',
    toggleDebug: language === 'ar' ? 'تبديل التصحيح' : 'Toggle Debug',
    strikes: language === 'ar' ? 'عدد سعر التنفيذ' : 'Strikes',
    strikes10: language === 'ar' ? '10 سعر التنفيذ' : '10 Strikes',
    strikes20: language === 'ar' ? '20 سعر التنفيذ' : '20 Strikes',
    strikes30: language === 'ar' ? '30 سعر التنفيذ' : '30 Strikes',
    strikesAll: language === 'ar' ? 'جميع سعر التنفيذ' : 'All Strikes',
    strikesCustom: language === 'ar' ? 'مخصص' : 'Custom',
    strikesFrom: language === 'ar' ? 'من' : 'From',
    strikesTo: language === 'ar' ? 'إلى' : 'To',
    summary: language === 'ar' ? 'ملخص' : 'Summary',
    strike: language === 'ar' ? 'سعر التنفيذ' : 'Strike',
    expiry: language === 'ar' ? 'اكسبايري' : 'Expiry',
    option: language === 'ar' ? 'اوبشن' : 'Option',
    options: language === 'ar' ? 'اوبشنز' : 'Options',
    calls: language === 'ar' ? 'الفرص' : 'Calls',
    puts: language === 'ar' ? 'البيع' : 'Puts',
    delta: language === 'ar' ? 'دلتا' : 'Delta',
    gamma: language === 'ar' ? 'جاما' : 'Gamma',
    theta: language === 'ar' ? 'ثيتا' : 'Theta',
    vega: language === 'ar' ? 'فيجا' : 'Vega',
    otm: language === 'ar' ? 'خارج المال' : 'OTM',
    atm: language === 'ar' ? 'عند المال' : 'ATM',
    itm: language === 'ar' ? 'داخل المال' : 'ITM',
    moneyness: language === 'ar' ? 'نقودية' : 'Moneyness',
    volatility: language === 'ar' ? 'تقلب' : 'Volatility',
    impliedVolatility: language === 'ar' ? 'التقلب الضمني' : 'Implied Volatility',
    openInterest: language === 'ar' ? 'الفائدة المفتوحة' : 'Open Interest',
    bidAskSpread: language === 'ar' ? 'فارق العرض/الطلب' : 'Bid/Ask Spread',
    liquidity: language === 'ar' ? 'سيولة' : 'Liquidity',
    leverage: language === 'ar' ? 'رافعة مالية' : 'Leverage',
    hedge: language === 'ar' ? 'تحوط' : 'Hedge',
    hedging: language === 'ar' ? 'تحوط' : 'Hedging',
    speculation: language === 'ar' ? 'تضارب' : 'Speculation',
    arbitrage: language === 'ar' ? 'مراجحة' : 'Arbitrage',
    flow: language === 'ar' ? 'تدفق' : 'Flow',
    flows: language === 'ar' ? 'تدفقات' : 'Flows',
    orderFlow: language === 'ar' ? 'تدفق الطلبات' : 'Order Flow',
    unusualActivity: language === 'ar' ? 'نشاط غير عادي' : 'Unusual Activity',
    unusualOptionsActivity: language === 'ar' ? 'نشاط اوبشن غير عادي' : 'Unusual Options Activity',
    gammaExposure: language === 'ar' ? 'تعرض جاما' : 'Gamma Exposure',
    deltaExposure: language === 'ar' ? 'تعرض دلتا' : 'Delta Exposure',
    vegaExposure: language === 'ar' ? 'تعرض فيجا' : 'Vega Exposure',
    thetaDecay: language === 'ar' ? 'اضمحلال ثيتا' : 'Theta Decay',
    gammaSqueeze: language === 'ar' ? 'ضغط جاما' : 'Gamma Squeeze',
    shortSqueeze: language === 'ar' ? 'ضغط البيع على المكشوف' : 'Short Squeeze',
    longSqueeze: language === 'ar' ? 'ضغط الشراء' : 'Long Squeeze',
    pinRisk: language === 'ar' ? 'خطر التثبيت' : 'Pin Risk',
    assignment: language === 'ar' ? 'تنفيذ' : 'Assignment',
    exercise: language === 'ar' ? 'ممارسة' : 'Exercise',
    expiration: language === 'ar' ? 'انتهاء الصلاحية' : 'Expiration',
    settlement: language === 'ar' ? 'تسوية' : 'Settlement',
    cashSettled: language === 'ar' ? 'تسوية نقدية' : 'Cash Settled',
    physicallySettled: language === 'ar' ? 'تسوية فيزيائية' : 'Physically Settled',
    europeanStyle: language === 'ar' ? 'أسلوب أوروبي' : 'European Style',
    americanStyle: language === 'ar' ? 'أسلوب أمريكي' : 'American Style',
    bermudaStyle: language === 'ar' ? 'أسلوب برمودا' : 'Bermuda Style',
    exotic: language === 'ar' ? 'غريب' : 'Exotic',
    vanilla: language === 'ar' ? 'فانيليا' : 'Vanilla',
    binary: language === 'ar' ? 'ثنائي' : 'Binary',
    barrier: language === 'ar' ? 'حاجز' : 'Barrier',
    knockIn: language === 'ar' ? 'دخول بالضرب' : 'Knock In',
    knockOut: language === 'ar' ? 'خروج بالضرب' : 'Knock Out',
    lookback: language === 'ar' ? 'نظرة خلفية' : 'Lookback',
    asian: language === 'ar' ? 'آسيوي' : 'Asian',
    basket: language === 'ar' ? 'سلة' : 'Basket',
    quanto: language === 'ar' ? 'كوانتو' : 'Quanto',
    spread: language === 'ar' ? 'انتشار' : 'Spread',
    straddle: language === 'ar' ? 'ركوب الحصان' : 'Straddle',
    strangle: language === 'ar' ? 'خنق' : 'Strangle',
    butterfly: language === 'ar' ? 'فراشة' : 'Butterfly',
    ironCondor: language === 'ar' ? 'كوندور حديدي' : 'Iron Condor',
    calendarSpread: language === 'ar' ? 'انتشار تقويمي' : 'Calendar Spread',
    diagonalSpread: language === 'ar' ? 'انتشار قطري' : 'Diagonal Spread',
    verticalSpread: language === 'ar' ? 'انتشار رأسي' : 'Vertical Spread',
    horizontalSpread: language === 'ar' ? 'انتشار أفقي' : 'Horizontal Spread',
    ratioSpread: language === 'ar' ? 'انتشار نسبي' : 'Ratio Spread',
    backspread: language === 'ar' ? 'انتشار خلفي' : 'Backspread',
    frontspread: language === 'ar' ? 'انتشار أمامي' : 'Frontspread',
    collar: language === 'ar' ? 'طوق' : 'Collar',
    protectivePut: language === 'ar' ? 'خيار بيع وقائي' : 'Protective Put',
    coveredCall: language === 'ar' ? 'خيار شراء مغطى' : 'Covered Call',
    nakedCall: language === 'ar' ? 'خيار شراء عاري' : 'Naked Call',
    nakedPut: language === 'ar' ? 'خيار بيع عاري' : 'Naked Put',
    cashSecuredPut: language === 'ar' ? 'خيار بيع مضمون نقداً' : 'Cash Secured Put',
    bullCallSpread: language === 'ar' ? 'انتشار خيار شراء صاعد' : 'Bull Call Spread',
    bearPutSpread: language === 'ar' ? 'انتشار خيار بيع هابط' : 'Bear Put Spread',
    longCall: language === 'ar' ? 'شراء خيار شراء' : 'Long Call',
    longPut: language === 'ar' ? 'شراء خيار بيع' : 'Long Put',
    shortCall: language === 'ar' ? 'بيع خيار شراء' : 'Short Call',
    shortPut: language === 'ar' ? 'بيع خيار بيع' : 'Short Put',
    syntheticLong: language === 'ar' ? 'طويل اصطناعي' : 'Synthetic Long',
    syntheticShort: language === 'ar' ? 'قصير اصطناعي' : 'Synthetic Short',
    syntheticStock: language === 'ar' ? 'سهم اصطناعي' : 'Synthetic Stock',
    boxSpread: language === 'ar' ? 'انتشار صندوق' : 'Box Spread',
    jellyRoll: language === 'ar' ? 'لفافة جيلي' : 'Jelly Roll',
    conversion: language === 'ar' ? 'تحويل' : 'Conversion',
    reversal: language === 'ar' ? 'انعكاس' : 'Reversal',
    fence: language === 'ar' ? 'سياج' : 'Fence',
    seagull: language === 'ar' ? 'نورس' : 'Seagull',
    condor: language === 'ar' ? 'كوندور' : 'Condor',
    albatross: language === 'ar' ? 'ألبطرس' : 'Albatross',
    ladder: language === 'ar' ? 'سلم' : 'Ladder',
    pyramid: language === 'ar' ? 'هرم' : 'Pyramid',
    diamond: language === 'ar' ? 'ماس' : 'Diamond',
    triangle: language === 'ar' ? 'مثلث' : 'Triangle',
    wedge: language === 'ar' ? 'إسفين' : 'Wedge',
    pennant: language === 'ar' ? 'راية' : 'Pennant',
    flag: language === 'ar' ? 'علم' : 'Flag',
    channel: language === 'ar' ? 'قناة' : 'Channel',
    range: language === 'ar' ? 'نطاق' : 'Range',
    breakout: language === 'ar' ? 'اختراق' : 'Breakout',
    breakdown: language === 'ar' ? 'انهيار' : 'Breakdown',
    support: language === 'ar' ? 'دعم' : 'Support',
    resistance: language === 'ar' ? 'مقاومة' : 'Resistance',
    trend: language === 'ar' ? 'اتجاه' : 'Trend',
    momentum: language === 'ar' ? 'زخم' : 'Momentum',
    consolidation: language === 'ar' ? 'توحيد' : 'Consolidation',
    accumulation: language === 'ar' ? 'تراكم' : 'Accumulation',
    distribution: language === 'ar' ? 'توزيع' : 'Distribution',
    divergence: language === 'ar' ? 'تباعد' : 'Divergence',
    convergence: language === 'ar' ? 'تقارب' : 'Convergence',
    correlation: language === 'ar' ? 'ارتباط' : 'Correlation',
    beta: language === 'ar' ? 'بيتا' : 'Beta',
    alpha: language === 'ar' ? 'ألفا' : 'Alpha',
    sharpeRatio: language === 'ar' ? 'نسبة شارب' : 'Sharpe Ratio',
    sortinoRatio: language === 'ar' ? 'نسبة سورتينو' : 'Sortino Ratio',
    treynorRatio: language === 'ar' ? 'نسبة ترينور' : 'Treynor Ratio',
    informationRatio: language === 'ar' ? 'نسبة المعلومات' : 'Information Ratio',
    calmarRatio: language === 'ar' ? 'نسبة كالمار' : 'Calmar Ratio',
    sterlingRatio: language === 'ar' ? 'نسبة ستيرلينغ' : 'Sterling Ratio',
    burkeRatio: language === 'ar' ? 'نسبة بورك' : 'Burke Ratio',
    kappaRatio: language === 'ar' ? 'نسبة كابا' : 'Kappa Ratio',
    omegaRatio: language === 'ar' ? 'نسبة أوميغا' : 'Omega Ratio',
    gainToPainRatio: language === 'ar' ? 'نسبة الربح للألم' : 'Gain to Pain Ratio',
    profitFactor: language === 'ar' ? 'عامل الربح' : 'Profit Factor',
    maxDrawdown: language === 'ar' ? 'أقصى انسحاب' : 'Maximum Drawdown',
    valueAtRisk: language === 'ar' ? 'القيمة المعرضة للخطر' : 'Value at Risk',
    conditionalValueAtRisk: language === 'ar' ? 'القيمة المشروطة المعرضة للخطر' : 'Conditional Value at Risk',
    expectedShortfall: language === 'ar' ? 'العجز المتوقع' : 'Expected Shortfall',
    tailRisk: language === 'ar' ? 'خطر الذيل' : 'Tail Risk',
    fatTails: language === 'ar' ? 'ذيول سمينة' : 'Fat Tails',
    skewness: language === 'ar' ? 'انحراف' : 'Skewness',
    kurtosis: language === 'ar' ? 'تفرطح' : 'Kurtosis',
    meanReversion: language === 'ar' ? 'عودة للمتوسط' : 'Mean Reversion',
    randomWalk: language === 'ar' ? 'مشي عشوائي' : 'Random Walk',
    efficientMarket: language === 'ar' ? 'سوق كفء' : 'Efficient Market',
    marketEfficiency: language === 'ar' ? 'كفاءة السوق' : 'Market Efficiency',
    weakFormEfficiency: language === 'ar' ? 'كفاءة الشكل الضعيف' : 'Weak Form Efficiency',
    semiStrongFormEfficiency: language === 'ar' ? 'كفاءة الشكل شبه القوي' : 'Semi-Strong Form Efficiency',
    strongFormEfficiency: language === 'ar' ? 'كفاءة الشكل القوي' : 'Strong Form Efficiency',
    anomaly: language === 'ar' ? 'شذوذ' : 'Anomaly',
    calendarEffect: language === 'ar' ? 'تأثير التقويم' : 'Calendar Effect',
    januaryEffect: language === 'ar' ? 'تأثير يناير' : 'January Effect',
    mondayEffect: language === 'ar' ? 'تأثير الاثنين' : 'Monday Effect',
    weekendEffect: language === 'ar' ? 'تأثير نهاية الأسبوع' : 'Weekend Effect',
    holidayEffect: language === 'ar' ? 'تأثير العطلة' : 'Holiday Effect',
    earningsEffect: language === 'ar' ? 'تأثير الأرباح' : 'Earnings Effect',
    dividendEffect: language === 'ar' ? 'تأثير الأرباح الموزعة' : 'Dividend Effect',
    splitEffect: language === 'ar' ? 'تأثير الانقسام' : 'Split Effect',
    mergerEffect: language === 'ar' ? 'تأثير الاندماج' : 'Merger Effect',
    acquisitionEffect: language === 'ar' ? 'تأثير الاستحواذ' : 'Acquisition Effect',
    ipoEffect: language === 'ar' ? 'تأثير الاكتتاب العام' : 'IPO Effect',
    delistingEffect: language === 'ar' ? 'تأثير الشطب' : 'Delisting Effect',
    bankruptcyEffect: language === 'ar' ? 'تأثير الإفلاس' : 'Bankruptcy Effect',
    restructuringEffect: language === 'ar' ? 'تأثير إعادة الهيكلة' : 'Restructuring Effect',
    buybackEffect: language === 'ar' ? 'تأثير إعادة الشراء' : 'Buyback Effect',
    dilutionEffect: language === 'ar' ? 'تأثير التخفيف' : 'Dilution Effect',
    insiderTrading: language === 'ar' ? 'تداول داخلي' : 'Insider Trading',
    frontRunning: language === 'ar' ? 'الركض الأمامي' : 'Front Running',
    spoofing: language === 'ar' ? 'التزوير' : 'Spoofing',
    layering: language === 'ar' ? 'الطبقات' : 'Layering',
    churning: language === 'ar' ? 'التحريك' : 'Churning',
    washTrading: language === 'ar' ? 'التداول المغسول' : 'Wash Trading',
    pumpAndDump: language === 'ar' ? 'الضخ والتفريغ' : 'Pump and Dump',
    shortAndDistort: language === 'ar' ? 'البيع على المكشوف والتشويه' : 'Short and Distort',
    cornering: language === 'ar' ? 'الزاوية' : 'Cornering',
    squeezes: language === 'ar' ? 'ضغوط' : 'Squeezes',
    volatilitySqueeze: language === 'ar' ? 'ضغط التقلب' : 'Volatility Squeeze',
    ivSqueeze: language === 'ar' ? 'ضغط التقلب الضمني' : 'IV Squeeze',
    deltaSqueeze: language === 'ar' ? 'ضغط دلتا' : 'Delta Squeeze',
    thetaSqueeze: language === 'ar' ? 'ضغط ثيتا' : 'Theta Squeeze',
    vegaSqueeze: language === 'ar' ? 'ضغط فيجا' : 'Vega Squeeze',
    rhoSqueeze: language === 'ar' ? 'ضغط رو' : 'Rho Squeeze',
    rho: language === 'ar' ? 'رو' : 'Rho',
    phi: language === 'ar' ? 'فاي' : 'Phi',
    psi: language === 'ar' ? 'بسي' : 'Psi',
    lambda: language === 'ar' ? 'لامدا' : 'Lambda',
    epsilon: language === 'ar' ? 'إبسيلون' : 'Epsilon',
    zeta: language === 'ar' ? 'زيتا' : 'Zeta',
    eta: language === 'ar' ? 'إيتا' : 'Eta',
    iota: language === 'ar' ? 'أيوتا' : 'Iota',
    kappa: language === 'ar' ? 'كابا' : 'Kappa',
    mu: language === 'ar' ? 'مو' : 'Mu',
    nu: language === 'ar' ? 'نو' : 'Nu',
    xi: language === 'ar' ? 'كسي' : 'Xi',
    omicron: language === 'ar' ? 'أوميكرون' : 'Omicron',
    pi: language === 'ar' ? 'باي' : 'Pi',
    sigma: language === 'ar' ? 'سيغما' : 'Sigma',
    tau: language === 'ar' ? 'تاو' : 'Tau',
    upsilon: language === 'ar' ? 'أبسيلون' : 'Upsilon',
    chi: language === 'ar' ? 'تشي' : 'Chi',
    omega: language === 'ar' ? 'أوميغا' : 'Omega'
  };

  const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 600, color: 'var(--txt)' };
  const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--muted)' };
  const selectStyle: React.CSSProperties = { background: 'var(--base)', color: 'var(--txt)', border: '1px solid var(--hair)', fontFamily: 'var(--font-ui)' };
  const tileStyle: React.CSSProperties = { background: 'var(--elev2)', border: '1px solid var(--hair)', borderRadius: 10 };
  const eyebrow: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted-2)' };
  const statValue: React.CSSProperties = { fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', marginTop: 4 };
  const debugTh: React.CSSProperties = { padding: '6px 10px', textAlign: 'end', fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', color: 'var(--muted)', fontFamily: 'var(--font-ui)', borderBottom: '1px solid var(--hair)', whiteSpace: 'nowrap' };
  const debugTd: React.CSSProperties = { padding: '6px 10px', textAlign: 'end' };

  // Process data with enhanced processing for consistent field names and calculations
  const processedData = useMemo(() => {
    return processOptionsData(data, effectiveDataMode);
  }, [data, effectiveDataMode]);

  // Get unique tickers and expiries from processed data
  const underlyings = useMemo(() => {
    const uniqueUnderlyings = Array.from(new Set(processedData.map(item => {
      const raw =
        item.underlyingSymbol ??
        (item as { Underlying?: string }).Underlying ??
        '';
      const mappedSymbol = getMappedSymbol(raw, isSymbolMappingEnabled);
      return mappedSymbol;
    }))).filter(Boolean).sort();
    return uniqueUnderlyings;
  }, [processedData]);

  // CBOE Spot Gamma pane: listen for strike/expiry changes and fetch when underlyings available
  useEffect(() => {
    const onStrikeOrExpiryChange = () => setSpotGammaFetchKey((k) => k + 1);
    window.addEventListener('stableStrikeOrExpiryChanged', onStrikeOrExpiryChange);
    window.addEventListener('stableDataFetchRequested', onStrikeOrExpiryChange);
    return () => {
      window.removeEventListener('stableStrikeOrExpiryChanged', onStrikeOrExpiryChange);
      window.removeEventListener('stableDataFetchRequested', onStrikeOrExpiryChange);
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const symbols = selectedUnderlying && selectedUnderlying !== 'all'
      ? [selectedUnderlying]
      : (underlyings?.length ? underlyings : ['SPY']);
    const expiryRange = localStorage.getItem('mmec_expiry_range') || 'weekly';
    dataService.fetchCboeGreeksData(symbols, { expiryRange }).then((cboeData) => {
      if (cancelled || !cboeData?.length) {
        if (!cancelled) setSpotGammaByStrike([]);
        return;
      }
      const fallbackS = getUnderlyingPriceFallback(cboeData, effectiveDataMode);
      const byStrike: Record<string, number> = {};
      for (const row of cboeData) {
        const strike = Number((row as unknown as Record<string, unknown>).Strike ?? (row as unknown as Record<string, unknown>)['Strike Price'] ?? 0) || 0;
        if (!strike) continue;
        const { spotGamma } = computeSpotGammaGexMme(row, effectiveDataMode, fallbackS);
        byStrike[strike] = (byStrike[strike] ?? 0) + spotGamma;
      }
      const arr = Object.entries(byStrike)
        .map(([strike, spotGamma]) => ({ strike, spotGamma }))
        .sort((a, b) => parseFloat(a.strike) - parseFloat(b.strike));
      if (!cancelled) setSpotGammaByStrike(arr);
    }).catch(() => { if (!cancelled) setSpotGammaByStrike([]); });
    return () => { cancelled = true; };
  }, [selectedUnderlying, effectiveDataMode, underlyings?.join(','), spotGammaFetchKey]);

  // Get all filter options using centralized getFilterOptions function
  const { availableSides, availableMoneyness, availableExpiries } = useMemo(() => {
    // Use existing getFilterOptions function for consistency
    return getFilterOptions(data);
  }, [data]);

  // Set default selected underlying if not set
  React.useEffect(() => {
    if (!selectedUnderlying && underlyings.length > 0) {
      setSelectedUnderlying(underlyings[0]);
    }
  }, [underlyings, selectedUnderlying]);

  // Set default selected expiry if not set
  React.useEffect(() => {
    if (!selectedExpiry && availableExpiries.length > 0) {
      setSelectedExpiry(availableExpiries[0]);
    }
  }, [availableExpiries, selectedExpiry]);

  const filteredData = useMemo(() => {
    if (hideFilters && prefilteredScannerRows != null) {
      return prefilteredScannerRows as ScannerData[];
    }
    if (hideFilters) {
      return processOptionsData(effectiveData, effectiveDataMode);
    }
    const filtered = filterLegacyVolumeScannerData(effectiveData, {
      selectedCategory,
      selectedUnderlying,
      selectedExpiry,
      selectedSide,
      selectedMoneyness,
      selectedDataMode: effectiveDataMode,
      isSymbolMappingEnabled,
    });
    return processOptionsData(filtered, effectiveDataMode);
  }, [
    effectiveData,
    hideFilters,
    prefilteredScannerRows,
    selectedCategory,
    selectedUnderlying,
    selectedExpiry,
    selectedSide,
    selectedMoneyness,
    effectiveDataMode,
    isSymbolMappingEnabled,
  ]);

  // Guard: reset invalid persisted selections (only when list is non-empty so we don't reset during refresh)
  useEffect(() => {
    if (underlyings.length > 0 && selectedUnderlying && selectedUnderlying !== 'all' && !underlyings.includes(selectedUnderlying)) {
      setSelectedUnderlying('all');
    }
    if (availableExpiries.length > 0 && selectedExpiry && selectedExpiry !== 'all' && !availableExpiries.includes(selectedExpiry)) {
      setSelectedExpiry('all');
    }
    if (selectedSide && selectedSide !== 'all' && !(availableSides || []).map(s => String(s).toUpperCase()).includes(String(selectedSide).toUpperCase())) {
      setSelectedSide('all');
    }
    if (selectedMoneyness && selectedMoneyness !== 'all' && !((availableMoneyness || []).map(String)).includes(String(selectedMoneyness))) {
      setSelectedMoneyness('all');
    }
  }, [underlyings, availableExpiries, availableSides, availableMoneyness, selectedUnderlying, selectedExpiry, selectedSide, selectedMoneyness]);

  const premiumChartData = useMemo(() => {
    if (filteredData.length === 0) return [];
    const agg = aggregateLegacyVolumeByStrike(filteredData, effectiveDataMode, isSymbolMappingEnabled);
    const mapped: PremiumData[] = agg.map((row) => {
      const netCallPremium = row.boughtCallPremium - row.soldCallPremium;
      const netPutPremium = row.soldPutPremium - row.boughtPutPremium;
      const totalNetPremium = netCallPremium + netPutPremium;
      const callFlowRatio =
        row.boughtCallPremium > 0 ? row.soldCallPremium / row.boughtCallPremium : 0;
      const putFlowRatio =
        row.boughtPutPremium > 0 ? row.soldPutPremium / row.boughtPutPremium : 0;
      const sentiment =
        totalNetPremium > 0
          ? Math.min(totalNetPremium / 1000000, 1)
          : Math.max(totalNetPremium / 1000000, -1);
      return {
        strike: row.strike,
        boughtCallPremium: row.boughtCallPremium,
        soldCallPremium: row.soldCallPremium,
        boughtPutPremium: row.boughtPutPremium,
        soldPutPremium: row.soldPutPremium,
        netCallPremium,
        netPutPremium,
        totalNetPremium,
        callFlowRatio,
        putFlowRatio,
        sentiment,
        underlyingPrice: row.underlyingPrice,
        netDelta: row.netDirectionalVolume,
        boughtCalls: row.boughtCalls,
        soldCalls: row.soldCalls,
        boughtPuts: row.boughtPuts,
        soldPuts: row.soldPuts,
        underlyingSymbol: row.underlyingSymbol,
      };
    });
    return filterStrikesByWindow(mapped, selectedStrikes, customStrikeFrom, customStrikeTo);
  }, [
    filteredData,
    effectiveDataMode,
    isSymbolMappingEnabled,
    selectedStrikes,
    customStrikeFrom,
    customStrikeTo,
  ]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalBoughtCallPremium = premiumChartData.reduce((sum, item) => sum + item.boughtCallPremium, 0);
    const totalSoldCallPremium = premiumChartData.reduce((sum, item) => sum + item.soldCallPremium, 0);
    const totalBoughtPutPremium = premiumChartData.reduce((sum, item) => sum + item.boughtPutPremium, 0);
    const totalSoldPutPremium = premiumChartData.reduce((sum, item) => sum + item.soldPutPremium, 0);
    
    // CORRECT LOGIC:
    // Total Bought Premium = Bought Calls (bullish) + Bought Puts (bearish)
    // Total Sold Premium = Sold Calls (bearish) + Sold Puts (bullish)
    // Net Flow = (Bought Calls + Sold Puts) - (Sold Calls + Bought Puts)
    // Positive = Bullish (more bullish flows), Negative = Bearish (more bearish flows)
    const totalBoughtPremium = totalBoughtCallPremium + totalBoughtPutPremium;
    const totalSoldPremium = totalSoldCallPremium + totalSoldPutPremium;
    // Calculate Net Delta = (Bought Calls + Sold Puts) - (Sold Calls + Bought Puts)
    // Positive = Bullish, Negative = Bearish
    // Use volume-based calculation to match VolumeDeltaReformed
    const totalBoughtCallVolume = premiumChartData.reduce((sum, item) => sum + item.boughtCalls, 0);
    const totalSoldCallVolume = premiumChartData.reduce((sum, item) => sum + item.soldCalls, 0);
    const totalBoughtPutVolume = premiumChartData.reduce((sum, item) => sum + item.boughtPuts, 0);
    const totalSoldPutVolume = premiumChartData.reduce((sum, item) => sum + item.soldPuts, 0);
    const netDelta = (totalBoughtCallVolume + totalSoldPutVolume) - (totalSoldCallVolume + totalBoughtPutVolume);
    
    // Net Call Flow = Bought Calls - Sold Calls
    // Net Put Flow = Sold Puts - Bought Puts (reversed for correct sentiment)
    const callNetFlow = totalBoughtCallPremium - totalSoldCallPremium;
    const putNetFlow = totalSoldPutPremium - totalBoughtPutPremium;
    
    return {
      totalBoughtCallPremium,
      totalSoldCallPremium,
      totalBoughtPutPremium,
      totalSoldPutPremium,
      totalBoughtPremium,
      totalSoldPremium,
      netDelta,
      callNetFlow,
      putNetFlow
    };
  }, [premiumChartData]);

  // Reformed color scheme: Green (bought calls), Light green (sold puts), Red (bought puts), Light red (sold calls)
  const ds = isDesignSystemOn();
  // Design System: colour by bias (D1) — bought calls + sold puts bullish = up, bought puts + sold calls bearish = down; puts lighter.
  const reformedColors = ds
    ? { boughtCalls: '#26a69a', soldPuts: 'rgba(38,166,154,0.5)', boughtPuts: 'rgba(239,83,80,0.5)', soldCalls: '#ef5350' }
    : {
    boughtCalls: '#10B981',      // Green
    soldPuts: '#34D399',         // Light green  
    boughtPuts: '#EF4444',       // Red
    soldCalls: '#F87171'         // Light red
  };
  /** Spot reference line: the system's dashed price line under the flag. */
  const priceLine = ds ? '#b2b5be' : t.accent;

  // Custom tooltip component that shows underlying symbol when filter is "all"
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const uSym = data?.underlyingSymbol;
      const showUnderlying =
        selectedUnderlying === 'all' && typeof uSym === 'string' && uSym.trim().length > 0 && uSym !== 'Unknown';
      
      return (
        <div style={{ background: 'var(--elev)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontFamily: 'var(--font-ui)', boxShadow: 'var(--lift-2)' }}>
          {showUnderlying && (
            <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--txt)' }}>
              {uSym}
            </p>
          )}
          <p style={{ margin: '0 0 4px', fontWeight: 500, color: 'var(--txt)' }}>
            {translations.strike}: {data?.strike || label}
          </p>
          
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: 0, color: entry.color, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
              {entry.name}: ${Number(entry.value).toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const uniqueUnderlyings = useMemo(
    () =>
      selectedUnderlying === 'all'
        ? collectDistinctMappedUnderlyings(filteredData, isSymbolMappingEnabled)
        : [],
    [filteredData, isSymbolMappingEnabled, selectedUnderlying]
  );

  // Series swatches only; underlying line is above the chart when filter is All
  const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap items-center gap-4 justify-center" style={{ color: 'var(--muted)', fontSize: 11 }}>
        {payload && payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded" 
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Get current underlying price for reference line
  const currentPrice = useMemo(() => {
    if (premiumChartData.length === 0) return null;
    const prices = premiumChartData.map(item => item.underlyingPrice).filter(price => price > 0);
    const underlyingPrice = prices.length > 0 ? prices[0] : null;
    
    if (!underlyingPrice) return null;
    
    // Find the closest strike to the underlying price
    const strikes = premiumChartData.map(item => parseFloat(item.strike));
    const closestStrike = strikes.reduce((closest, strike) => {
      return Math.abs(strike - underlyingPrice) < Math.abs(closest - underlyingPrice) ? strike : closest;
    });
    return closestStrike.toString();
  }, [premiumChartData]);

  // Calculate sentiment at current price based on volume data
  const sentimentData = useMemo(() => {
    if (!premiumChartData || premiumChartData.length === 0) return { bullishValue: 0, bearishValue: 0 };
    
    // Find the strike closest to current price
    if (!currentPrice) return { bullishValue: 0, bearishValue: 0 };
    
    const currentPriceNum = parseFloat(currentPrice);
    const priceRange = currentPriceNum * 0.05; // 5% range
    
    // Find strikes near current price
    const nearPriceData = premiumChartData.filter(item => {
      const strike = parseFloat(item.strike);
      return Math.abs(strike - currentPriceNum) <= priceRange;
    });

    if (nearPriceData.length === 0) return { bullishValue: 0, bearishValue: 0 };

    // Calculate bullish vs bearish pressure based on volume
    // Bullish = bought calls + sold puts (buying upside, selling downside protection)
    // Bearish = sold calls + bought puts (selling upside, buying downside protection)
    let bullishPressure = 0;
    let bearishPressure = 0;

    nearPriceData.forEach(item => {
      // Bought calls = bullish pressure
      bullishPressure += item.boughtCalls;
      // Sold puts = bullish pressure (selling downside protection)
      bullishPressure += item.soldPuts;
      
      // Sold calls = bearish pressure (selling upside)
      bearishPressure += item.soldCalls;
      // Bought puts = bearish pressure (buying downside protection)
      bearishPressure += item.boughtPuts;
    });

    return { bullishValue: bullishPressure, bearishValue: bearishPressure };
  }, [premiumChartData, currentPrice, effectiveDataMode]);

  return (
    <div className={`p-2 sm:p-4 relative w-full max-w-full${ds ? ' tcr-ds mmec' : ''}`} style={{ background: 'var(--elev)', border: '1px solid var(--hair)', borderRadius: 'var(--radius-md, 10px)', color: 'var(--txt)', fontFamily: 'var(--font-ui)' }}> 
      <span className={`absolute top-4 right-4 text-xs text-gray-400 select-all${ds ? ' tcr-pageid' : ''}`} style={{padding: '4px 8px'}}>
        Page ID: TRADES_CHART_REFORMED
      </span>
      <h2 className="mb-4" style={{ fontSize: 18, fontWeight: ds ? 700 : 600, color: 'var(--txt)' }}>{translations.title}</h2>

      {/* Historical Data Selector */}
      {!hideFilters && (
        <div className="mb-4">
          <HistoricalDataSelector isDarkMode={isDarkMode} />
        </div>
      )}

      {/* Sentiment Bar - Show winning direction at current price */}
      <div className="mb-4">
        <SentimentBar
          bullishValue={sentimentData.bullishValue}
          bearishValue={sentimentData.bearishValue}
          label={translations.currentPrice}
          showLegend={true}
          height={40}
        />
      </div>

      {/* Main Chart - Stacked Bars */}
      {selectedUnderlying === 'all' && uniqueUnderlyings.length > 0 && (
        <div className="text-center mb-2" style={{ fontSize: 12.5, color: 'var(--txt-2)' }}>
          <span className="font-semibold">{language === 'ar' ? 'الرمز الأساسي:' : 'Underlying:'}</span>{' '}
          <span className="font-bold">{uniqueUnderlyings.join(', ')}</span>
        </div>
      )}
      <div className="w-full max-w-full overflow-x-auto rounded-lg mb-4" style={{ minHeight: '400px' }}>
        <div className="w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] xl:h-[800px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={premiumChartData} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 4" stroke={t.grid} />
              <XAxis 
                dataKey="strike" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                interval={0}
                tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
                yAxisId="left"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              
              {/* Reformed stacked bars - all in one stack per strike */}
              <Bar dataKey="boughtCallPremium" stackId="premium" fill={reformedColors.boughtCalls} name="Bought Call Premium" radius={[4, 4, 0, 0]} yAxisId="left" onClick={onBarClick} cursor="pointer" />
              <Bar dataKey="soldPutPremium" stackId="premium" fill={reformedColors.soldPuts} name="Sold Put Premium" radius={[4, 4, 0, 0]} yAxisId="left" onClick={onBarClick} cursor="pointer" />
              <Bar dataKey="boughtPutPremium" stackId="premium" fill={reformedColors.boughtPuts} name="Bought Put Premium" radius={[4, 4, 0, 0]} yAxisId="left" onClick={onBarClick} cursor="pointer" />
              <Bar dataKey="soldCallPremium" stackId="premium" fill={reformedColors.soldCalls} name="Sold Call Premium" radius={[4, 4, 0, 0]} yAxisId="left" onClick={onBarClick} cursor="pointer" />
              
              {/* Current Price Reference Line - Moved after bars for higher z-index */}
              {currentPrice && (
                <ReferenceLine 
                  x={currentPrice.toString()} 
                  stroke={priceLine} 
                  strokeWidth={3}
                  strokeDasharray="3 4"
                  label={{ 
                    value: `${translations.currentPrice}: ${currentPrice}`, 
                    position: "top",
                    fill: t.text,
                    fontSize: 11
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Net Premium Pane - Taller */}
      <div className="w-full max-w-full overflow-x-auto rounded-lg mb-4" style={{ minHeight: '300px' }}>
        <h3 className="mb-2" style={sectionTitle}>
          {translations.netPremium}
        </h3>
        <div className="w-full h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={premiumChartData} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 4" stroke={t.grid} />
              <XAxis 
                dataKey="strike" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                interval={0}
                tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Zero line */}
              <ReferenceLine y={0} stroke={t.muted} strokeDasharray="3 4" />
              
              {/* Net Premium Bars - Green above zero, Red below zero */}
              <Bar 
                dataKey="totalNetPremium" 
                fill={t.up}
                name={translations.totalNetPremium}
                onClick={onBarClick}
                cursor="pointer"
              >
                {premiumChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.totalNetPremium >= 0 ? t.up : t.down} />
                ))}
              </Bar>
              <Legend wrapperStyle={{ fontSize: 11, color: 'var(--muted)' }} />
              
              {/* Current Price Reference Line */}
              {currentPrice && (
                <ReferenceLine 
                  x={currentPrice.toString()} 
                  stroke={priceLine} 
                  strokeWidth={2}
                  strokeDasharray="3 4"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Net Delta Pane - New below Net Premium */}
      <div className="w-full max-w-full overflow-x-auto rounded-lg mb-4" style={{ minHeight: '240px' }}>
        <h3 className="mb-2" style={sectionTitle}>
          {translations.netDelta}
        </h3>
        <div className="w-full h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={premiumChartData} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 4" stroke={t.grid} />
              <XAxis 
                dataKey="strike" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                interval={0}
                tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke={t.muted} strokeDasharray="3 4" />
              {/* Use actual Net Delta values */}
              <Bar 
                dataKey="netDelta" 
                fill={t.up}
                name={translations.netDelta}
                onClick={onBarClick}
                cursor="pointer"
              >
                {premiumChartData.map((entry, index) => (
                  <Cell key={`cell-nd-${index}`} fill={entry.netDelta >= 0 ? t.up : t.down} />
                ))}
              </Bar>
              <Legend wrapperStyle={{ fontSize: 11, color: 'var(--muted)' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Spot Gamma Pane - below Net Delta (CBOE data) */}
      {spotGammaByStrike.length > 0 && (
        <div className="w-full max-w-full overflow-x-auto rounded-lg mb-4" style={{ minHeight: '240px' }}>
          <h3 className="mb-2" style={sectionTitle}>
            {translations.spotGamma}
          </h3>
          <div className="w-full h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={spotGammaByStrike} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 4" stroke={t.grid} />
                <XAxis 
                  dataKey="strike" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  interval={0}
                  tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                  axisLine={{ stroke: t.border }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: t.text, fontFamily: 'var(--font-mono)' }}
                  axisLine={{ stroke: t.border }}
                  tickLine={false}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const p = payload[0]?.payload;
                      return (
                        <div style={{ background: 'var(--elev)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontFamily: 'var(--font-ui)', boxShadow: 'var(--lift-2)' }}>
                          <p style={{ margin: '0 0 4px', fontWeight: 500, color: 'var(--txt)' }}>{translations.strike}: {p?.strike}</p>
                          <p style={{ margin: 0, color: 'var(--txt-2)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{translations.spotGamma}: {Number(p?.spotGamma).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <ReferenceLine y={0} stroke={t.muted} strokeDasharray="3 4" />
                <Bar dataKey="spotGamma" name={translations.spotGamma} onClick={onBarClick} cursor="pointer">
                  {spotGammaByStrike.map((entry, index) => (
                    <Cell key={`cell-sg-${index}`} fill={(entry.spotGamma ?? 0) >= 0 ? t.up : t.down} />
                  ))}
                </Bar>
                <Legend wrapperStyle={{ fontSize: 11, color: 'var(--muted)' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters second */}
      {!hideFilters && (
        <div className="mb-6">
          {/* Category Filter */}
          <div className="mb-3">
            <label className="block mb-1" style={labelStyle}>
              {language === 'ar' ? 'الفئة' : 'Category'}
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as 'all' | 'stocks' | 'etfs' | 'indices')}
              className="w-32 text-xs px-2 py-1 rounded" style={selectStyle}
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              <option value="stocks">{language === 'ar' ? 'الأسهم' : 'Stocks'}</option>
              <option value="etfs">{language === 'ar' ? 'صناديق الاستثمار' : 'ETFs'}</option>
              <option value="indices">{language === 'ar' ? 'المؤشرات' : 'Indices'}</option>
            </select>
          </div>
          
          <StandardFilterBar
            selectedUnderlying={selectedUnderlying}
            selectedExpiry={selectedExpiry}
            selectedSide={selectedSide}
            selectedMoneyness={selectedMoneyness}
            selectedDataMode={selectedDataMode}
            availableUnderlyings={underlyings}
            availableExpiries={availableExpiries}
            availableSides={availableSides}
            availableMoneyness={availableMoneyness}
            onUnderlyingChange={setSelectedUnderlying}
            onExpiryChange={setSelectedExpiry}
            onSideChange={setSelectedSide}
            onMoneynessChange={setSelectedMoneyness}
            onDataModeChange={handleDataModeChange}
            showDataModeFilter={true}
            compact={false}
          />
          
          {/* Strikes Filter */}
          <div className="mt-4 flex items-center gap-4">
            <label style={labelStyle}>
              {translations.strikes}:
            </label>
            <select
              value={selectedStrikes}
              onChange={(e) => setSelectedStrikes(e.target.value as '10' | '20' | '30' | 'all' | 'custom')}
              className="px-3 py-2 rounded text-sm" style={selectStyle}
            >
              <option value="all">{translations.strikesAll}</option>
              <option value="10">{translations.strikes10}</option>
              <option value="20">{translations.strikes20}</option>
              <option value="30">{translations.strikes30}</option>
              <option value="custom">{translations.strikesCustom}</option>
            </select>
            
            {/* Custom Strikes Input Boxes */}
            {selectedStrikes === 'custom' && (
              <div className="flex gap-2 items-center">
                <div>
                  <label className="block mb-1" style={labelStyle}>
                    {translations.strikesFrom}
                  </label>
                  <input
                    type="number"
                    value={customStrikeFrom}
                    onChange={(e) => setCustomStrikeFrom(e.target.value)}
                    placeholder="6500"
                    className="w-20 text-xs px-2 py-1 rounded" style={selectStyle}
                  />
                </div>
                <div>
                  <label className="block mb-1" style={labelStyle}>
                    {translations.strikesTo}
                  </label>
                  <input
                    type="number"
                    value={customStrikeTo}
                    onChange={(e) => setCustomStrikeTo(e.target.value)}
                    placeholder="6600"
                    className="w-20 text-xs px-2 py-1 rounded" style={selectStyle}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="text-center p-3" style={tileStyle}>
          <div style={eyebrow}>{translations.totalBoughtPremium}</div>
          <div style={{ ...statValue, color: summaryStats.totalBoughtPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
            ${summaryStats.totalBoughtPremium.toLocaleString()}
          </div>
        </div>
        <div className="text-center p-3" style={tileStyle}>
          <div style={eyebrow}>{translations.totalSoldPremium}</div>
          <div style={{ ...statValue, color: summaryStats.totalSoldPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
            ${summaryStats.totalSoldPremium.toLocaleString()}
          </div>
        </div>
        <div className="text-center p-3" style={tileStyle}>
          <div style={eyebrow}>{translations.netDelta}</div>
          <div style={{ ...statValue, color: summaryStats.netDelta >= 0 ? 'var(--up)' : 'var(--down)' }}>
            ${summaryStats.netDelta.toLocaleString()}
          </div>
        </div>
        <div className="text-center p-3" style={tileStyle}>
          <div style={eyebrow}>{summaryStats.netDelta >= 0 ? translations.bullishPressure : translations.bearishPressure}</div>
          <div style={{ ...statValue, color: summaryStats.netDelta >= 0 ? 'var(--up)' : 'var(--down)' }}>
            {Math.abs(summaryStats.netDelta).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Debug Table Toggle */}
      <div className="flex justify-center mb-4">
        <button
          onClick={() => setShowDebugTable(!showDebugTable)}
          className="mmec-btn"
        >
          {showDebugTable ? 'Hide' : 'Show'} {translations.debugTable}
        </button>
      </div>

      {/* Debug Table */}
      {showDebugTable && (
        <div className="overflow-x-auto p-4" style={{ background: 'var(--base)', border: '1px solid var(--hair)', borderRadius: 10 }}>
          <h3 className="mb-3" style={sectionTitle}>{translations.debugTable}</h3>
          <table className="w-full" style={{ fontSize: 12.5, color: 'var(--txt)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr style={{ background: 'var(--elev)' }}>
                <th style={debugTh}>Strike</th>
                <th style={debugTh}>Bought Call</th>
                <th style={debugTh}>Sold Call</th>
                <th style={debugTh}>Bought Put</th>
                <th style={debugTh}>Sold Put</th>
                <th style={debugTh}>Net Call</th>
                <th style={debugTh}>Net Put</th>
                <th style={debugTh}>Total Net</th>
                <th style={debugTh}>Sentiment</th>
              </tr>
            </thead>
            <tbody>
              {premiumChartData.map((item, index) => (
                <tr key={index} style={{ background: index % 2 === 0 ? 'var(--elev)' : 'transparent', borderBottom: '1px solid var(--hair)' }}>
                  <td style={{ ...debugTd, fontWeight: 600 }}>{item.strike}</td>
                  <td style={{ ...debugTd, color: item.boughtCallPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.boughtCallPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.soldCallPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.soldCallPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.boughtPutPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.boughtPutPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.soldPutPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.soldPutPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.netCallPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.netCallPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.netPutPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.netPutPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.totalNetPremium >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    ${item.totalNetPremium.toLocaleString()}
                  </td>
                  <td style={{ ...debugTd, color: item.sentiment >= 0 ? 'var(--up)' : 'var(--down)' }}>
                    {item.sentiment.toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Explanation Section - always at the bottom */}
      <ToolExplanation
        title={translations.title}
        whatIsThis={language === 'ar' ? 'يعرض هذا الرسم تدفق العلاوة الصافي عبر أسعار التنفيذ، مع تفصيل علاوة الشراء/البيع للفرص والبيع مع صافي العلاوة.' : 'This chart shows net premium flow across strikes, with a breakdown of bought/sold premiums for calls and puts with net premium visualization.'}
        howToUse={language === 'ar' ? 'ابدأ بتحديد السهم وتاريخ الانتهاء. راقب صافي التدفق الإيجابي (ضغط شرائي) أو السلبي (ضغط بيعي) في لوحة صافي العلاوة لتحديد مناطق الجذب.' : 'Select the underlying and expiry. Watch for positive net flow (buy pressure) or negative net flow (sell pressure) in the net premium pane to identify attraction zones.'}
        example={language === 'ar' ? 'إذا كان صافي التدفق +$1.2M عند سعر التنفيذ 500 مع زيادة شراء خيارات الشراء وبيع البيع، فمن المحتمل أن ينجذب السعر نحو 500.' : 'If net flow is +$1.2M at strike 500 with rising bought calls and sold puts, price likely gravitates toward 500.'}
      />
    </div>
  );
};

export default TradesChartReformed;
